import { useMutation, useQueryClient } from '@tanstack/react-query'
import APIServices from '@/api/APIServices'
import { ShipmentNotification, ShipmentNotificationParams } from '@/interfaces/shipmentNotification'
import axios, { AxiosError } from 'axios'

interface UpdateShipmentNotificationParams {
  id: number
  data: Partial<ShipmentNotificationParams>
}

export const useUpdateShipmentNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ShipmentNotificationParams }) => {
      try {
        const response = await APIServices.patch(`/cargo/shippingnotification/${id}`, data)
        
        // Handle different response formats
        if (response.data && response.data.message) {
          // API returned success message, create simulated response
          return {
            id,
            ...data,
            status: data.status || 'CREATED',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: { id: 1, displayName: 'Usuario' },
            providerCompany: { id: data.providerCompanyId, name: 'Proveedor' },
            managerCompany: { id: data.managerCompanyId, name: 'Manager' }
          }
        } else if (response.data && response.data.id) {
          // API returned complete notification
          return response.data
        } else {
          // Unexpected response format, create simulated response
          return {
            id,
            ...data,
            status: data.status || 'CREATED',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: { id: 1, displayName: 'Usuario' },
            providerCompany: { id: data.providerCompanyId, name: 'Proveedor' },
            managerCompany: { id: data.managerCompanyId, name: 'Manager' }
          }
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError
          const errorMessage = axiosError.response?.data && typeof axiosError.response.data === 'object' && 'message' in axiosError.response.data 
            ? (axiosError.response.data as any).message 
            : axiosError.message
          throw new Error(`Error updating notification: ${errorMessage}`)
        }
        throw error
      }
    },
    onSuccess: (updatedNotification, variables) => {
      // Update cache manually to ensure the notification remains visible
      queryClient.setQueryData(
        ['shipment-notifications', 'with-names'],
        (currentData: ShipmentNotification[] | undefined) => {
          if (!currentData) return [updatedNotification]
          
          const updatedData = currentData.map(notification => 
            notification.id === variables.id ? updatedNotification : notification
          )
          
          return updatedData
        }
      )
      // Invalidar queries para refrescar la tabla
      queryClient.invalidateQueries({ queryKey: ['shipment-notifications'] });
    },
    onError: (error) => {
      console.error('Error updating shipment notification:', error)
    }
  })
}
